import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Trash2, RotateCw, AlertCircle } from 'lucide-react';
import { API_BASE } from '../services/apiBase';

const API = API_BASE;
const TENANT = '569593dd-38e2-4d69-bdca-5d07be8b46f1';

const GENRES    = ['Afrikaans','English','Golden Oldies','Country','Gospel','CCM','Dance','Christmas','Jazz','RnB','Pop','Rock','Instrumental','Classical','Worship','Kids','Other'];
const LANGUAGES = ['Afrikaans','English','Zulu','Xhosa','Sotho','Other'];
const MOODS     = ['Happy','Calm','Energetic','Romantic','Uplifting','Melancholic','Festive','Background'];

function authHeaders() { return { Authorization: 'Bearer ' + localStorage.getItem('access_token') }; }

function fmtDuration(s) {
  if (!s) return '--:--';
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function fmtLibDuration(secs) {
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const FP_COLOR = {
  identified: '#22c55e', not_found: '#8b949e', pending: '#a78bfa', processing: '#f59e0b',
};
const FP_LABEL = {
  identified: 'Identified', not_found: 'Unknown', pending: 'Pending', processing: 'Scanning…',
};

function EnergyBar({ value }) {
  const colors = ['', '#22c55e', '#84cc16', '#f59e0b', '#f97316', '#ef4444'];
  return (
    <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {[1,2,3,4,5].map(n => (
        <div key={n} style={{ width: 5, height: 14, borderRadius: 2, background: n <= value ? colors[value] : '#21262d' }} />
      ))}
    </div>
  );
}

// ── Upload zone ───────────────────────────────────────────────────────────────
function UploadZone({ onUploaded }) {
  const [dragging, setDragging] = useState(false);
  const [uploads, setUploads]   = useState([]);
  const inputRef = useRef();

  const uploadFiles = async (files) => {
    const fileArr = Array.from(files).filter(f =>
      f.type.startsWith('audio/') || f.name.match(/\.(mp3|wav|flac|aac|ogg|m4a|wma|aiff)$/i)
    );
    if (!fileArr.length) { alert('Please select audio files only.'); return; }
    const newUploads = fileArr.map(f => ({ name: f.name, status: 'uploading', progress: 0, result: null }));
    setUploads(prev => [...newUploads, ...prev]);
    for (let i = 0; i < fileArr.length; i++) {
      const file = fileArr[i];
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tenant_id', TENANT);
      try {
        const res = await axios.post(`${API}/music/tracks/upload`, fd, {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
          onUploadProgress: e => {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploads(prev => prev.map((u, idx) => idx === i ? { ...u, progress: pct } : u));
          },
        });
        setUploads(prev => prev.map((u, idx) => idx === i ? { ...u, status: 'done', result: res.data } : u));
        onUploaded();
      } catch (e) {
        setUploads(prev => prev.map((u, idx) => idx === i ? { ...u, status: 'error', error: String(e.response?.data?.detail || e.message) } : u));
      }
    }
  };

  const onDrop = useCallback(e => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); }, []);

  return (
    <div style={{ padding: '0 0 16px' }}>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? '#2f81f7' : '#30363d'}`,
          borderRadius: 10, padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
          background: dragging ? 'rgba(47,129,247,0.05)' : '#161b22', transition: 'all 0.2s',
        }}
      >
        <input ref={inputRef} type="file" multiple accept="audio/*,.mp3,.wav,.flac,.aac,.ogg,.m4a" style={{ display: 'none' }} onChange={e => uploadFiles(e.target.files)} />
        <div style={{ fontSize: 22, marginBottom: 8 }}>🎵</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#e6edf3', marginBottom: 4 }}>Drop audio files here or click to browse</div>
        <div style={{ fontSize: 12, color: '#8b949e' }}>MP3, WAV, FLAC, AAC, OGG, M4A · Auto-fingerprinted via AcoustID + Spotify</div>
      </div>
      {uploads.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {uploads.map((u, i) => (
            <div key={i} style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8, padding: '8px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: u.status === 'uploading' ? 5 : 0 }}>
                <span style={{ fontSize: 12, color: '#e6edf3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}>{u.name}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: u.status === 'done' ? '#22c55e' : u.status === 'error' ? '#ef4444' : '#f59e0b', marginLeft: 12, flexShrink: 0 }}>
                  {u.status === 'uploading' ? `${u.progress}%` : u.status === 'done' ? 'DONE' : 'ERROR'}
                </span>
              </div>
              {u.status === 'uploading' && (
                <div style={{ height: 3, background: '#21262d', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${u.progress}%`, background: '#2f81f7', transition: 'width 0.2s' }} />
                </div>
              )}
              {u.status === 'error' && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>{u.error}</div>}
              {u.result && u.result.fingerprint_status === 'identified' && (
                <div style={{ fontSize: 11, color: '#8b949e', marginTop: 3 }}>
                  {u.result.artist} – {u.result.title}
                  {u.result.bpm && <span style={{ marginLeft: 10, color: '#a78bfa' }}>{u.result.bpm} BPM</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────────────
function EditModal({ track, onSave, onClose }) {
  const [form, setForm] = useState({
    title: track.title || '', artist: track.artist || '', album: track.album || '',
    genre: track.genre || '', language: track.language || '', mood: track.mood || '',
    bpm: track.bpm || '', energy_level: track.energy_level || null,
    year: track.year || '', isrc: track.isrc || '', status: track.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/music/tracks/${track.id}`, { ...form, tenant_id: TENANT }, { headers: authHeaders() });
      onSave();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  const iStyle = { width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, padding: '8px 12px', color: '#e6edf3', fontSize: 13, boxSizing: 'border-box', outline: 'none' };
  const lStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em', marginBottom: 5, textTransform: 'uppercase' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 14, padding: 28, width: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#e6edf3' }}>Edit Track</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8b949e', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {[['Title','title',true],['Artist','artist'],['Album','album'],['Year','year'],['ISRC','isrc',true]].map(([label,key,full]) => (
            <div key={key} style={full ? { gridColumn: '1/-1' } : {}}>
              <label style={lStyle}>{label}</label>
              <input value={form[key]} onChange={f(key)} style={iStyle} />
            </div>
          ))}
          <div><label style={lStyle}>Genre</label>
            <select value={form.genre} onChange={f('genre')} style={iStyle}>
              <option value="">—</option>
              {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div><label style={lStyle}>Language</label>
            <select value={form.language} onChange={f('language')} style={iStyle}>
              <option value="">—</option>
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div><label style={lStyle}>Mood</label>
            <select value={form.mood} onChange={f('mood')} style={iStyle}>
              <option value="">—</option>
              {MOODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div><label style={lStyle}>BPM</label>
            <input type="number" value={form.bpm} onChange={f('bpm')} style={iStyle} placeholder="e.g. 128" />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={lStyle}>Energy Level</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {[1,2,3,4,5].map(n => {
                const cols = ['', '#22c55e', '#84cc16', '#f59e0b', '#f97316', '#ef4444'];
                const lbs  = ['', 'Very Calm', 'Calm', 'Medium', 'Energetic', 'Very Energetic'];
                return (
                  <button key={n} title={lbs[n]} onClick={() => setForm(p => ({ ...p, energy_level: n }))} style={{
                    width: 36, height: 36, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                    border: `2px solid ${form.energy_level === n ? cols[n] : '#30363d'}`,
                    background: form.energy_level === n ? cols[n] + '22' : '#0d1117',
                    color: form.energy_level === n ? cols[n] : '#484f58',
                  }}>{n}</button>
                );
              })}
            </div>
          </div>
          <div><label style={lStyle}>Status</label>
            <select value={form.status} onChange={f('status')} style={iStyle}>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #30363d', background: 'transparent', color: '#8b949e', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: saving ? '#21262d' : '#2f81f7', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Track detail panel ────────────────────────────────────────────────────────
function DetailPanel({ track, onEdit, onDelete, onReScan, navigate }) {
  const fpColor = FP_COLOR[track.fingerprint_status] || '#8b949e';
  const fpLabel = FP_LABEL[track.fingerprint_status] || 'Unknown';

  const Field = ({ label, value, mono }) => value ? (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#8b949e', letterSpacing: '0.08em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#e6edf3', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</div>
    </div>
  ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#161b22', borderLeft: '1px solid #30363d' }}>
      {/* Hero */}
      <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid #30363d', background: 'linear-gradient(180deg, #1c2333 0%, #161b22 100%)' }}>
        <div style={{
          width: 64, height: 64, borderRadius: 12, marginBottom: 16,
          background: 'linear-gradient(135deg, #2f81f7 0%, #a78bfa 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 26,
        }}>🎵</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#e6edf3', lineHeight: '21px', marginBottom: 4 }}>
          {track.title || 'Untitled'}
        </div>
        <div style={{ fontSize: 13, color: '#8b949e', marginBottom: 12 }}>
          {track.artist || '—'}
        </div>

        {/* Quick chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {track.duration_seconds && (
            <span style={{ padding: '3px 10px', borderRadius: 20, background: '#21262d', fontSize: 11, color: '#8b949e' }}>
              {fmtDuration(track.duration_seconds)}
            </span>
          )}
          {track.bpm && (
            <span style={{ padding: '3px 10px', borderRadius: 20, background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)', fontSize: 11, color: '#a78bfa' }}>
              {track.bpm} BPM
            </span>
          )}
          {track.mood && (
            <span style={{ padding: '3px 10px', borderRadius: 20, background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.25)', fontSize: 11, color: '#38bdf8' }}>
              {track.mood}
            </span>
          )}
          {track.genre && (
            <span style={{ padding: '3px 10px', borderRadius: 20, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)', fontSize: 11, color: '#22c55e' }}>
              {track.genre}
            </span>
          )}
        </div>
      </div>

      {/* Scrollable details */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

        {/* Fingerprint status */}
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: fpColor + '12', border: `1px solid ${fpColor}30` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#8b949e', letterSpacing: '0.08em', marginBottom: 2 }}>FINGERPRINT</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: fpColor }}>{fpLabel}</div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#21262d', color: track.status === 'active' ? '#22c55e' : '#8b949e' }}>
              {track.status?.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Energy */}
        {track.energy_level && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#8b949e', letterSpacing: '0.08em', marginBottom: 6 }}>ENERGY</div>
            <EnergyBar value={track.energy_level} />
          </div>
        )}

        <Field label="ALBUM"    value={track.album} />
        <Field label="YEAR"     value={track.year} />
        <Field label="LANGUAGE" value={track.language} />
        <Field label="PLAYS"    value={track.play_count != null ? String(track.play_count) : null} />
        <Field label="ISRC"     value={track.isrc} mono />
        <Field label="SPOTIFY ID"    value={track.spotify_id} mono />
        <Field label="MUSICBRAINZ"   value={track.musicbrainz_id} mono />
        <Field label="ACOUSTID"      value={track.acoustid} mono />

        {/* Audio features */}
        {(track.danceability != null || track.valence != null) && (
          <div style={{ margin: '4px 0 12px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#8b949e', letterSpacing: '0.08em', marginBottom: 8 }}>AUDIO FEATURES</div>
            {[
              ['Danceability', track.danceability],
              ['Valence', track.valence],
              ['Acousticness', track.acousticness],
            ].filter(([, v]) => v != null).map(([label, val]) => (
              <div key={label} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8b949e', marginBottom: 3 }}>
                  <span>{label}</span><span>{(val * 100).toFixed(0)}%</span>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: '#21262d', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${val * 100}%`, background: '#2f81f7', borderRadius: 2 }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ padding: '16px 20px', borderTop: '1px solid #30363d', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Analyze with AI - primary CTA */}
        <button
          onClick={() => navigate('/dj-studio')}
          style={{
            width: '100%', padding: '11px', borderRadius: 10, border: 'none',
            background: 'linear-gradient(135deg, #2f81f7, #7c3aed)',
            color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700,
            letterSpacing: '0.02em', boxShadow: '0 4px 14px rgba(47,129,247,0.3)',
          }}
        >
          ✦ Analyze with AI
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onEdit(track)} style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid #30363d', background: '#21262d', color: '#e6edf3', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            Edit
          </button>
          <button onClick={() => onReScan(track.id)} style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.08)', color: '#f59e0b', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            Re-scan
          </button>
          <button onClick={() => onDelete(track.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Track table row ───────────────────────────────────────────────────────────
function TrackRow({ track, selected, onClick }) {
  const [hovered, setHovered] = useState(false);
  const fpColor = FP_COLOR[track.fingerprint_status] || '#484f58';
  const isActive = track.status === 'active';

  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer',
        background: selected ? 'rgba(47,129,247,0.1)' : hovered ? '#21262d' : 'transparent',
        borderBottom: '1px solid #21262d',
        borderLeft: selected ? '2px solid #2f81f7' : '2px solid transparent',
        transition: 'background 0.1s',
      }}
    >
      <td style={{ padding: '10px 14px', maxWidth: 200 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e6edf3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {track.title || <span style={{ color: '#484f58', fontStyle: 'italic' }}>Untitled</span>}
        </div>
      </td>
      <td style={{ padding: '10px 14px', maxWidth: 160 }}>
        <div style={{ fontSize: 13, color: '#8b949e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {track.artist || '—'}
        </div>
      </td>
      <td style={{ padding: '10px 14px', width: 60, textAlign: 'center' }}>
        {track.bpm ? (
          <span style={{ fontSize: 12, color: '#a78bfa', fontVariantNumeric: 'tabular-nums' }}>{track.bpm}</span>
        ) : <span style={{ color: '#484f58' }}>—</span>}
      </td>
      <td style={{ padding: '10px 14px', width: 100 }}>
        {track.mood ? (
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'rgba(56,189,248,0.1)', color: '#38bdf8' }}>{track.mood}</span>
        ) : <span style={{ color: '#484f58' }}>—</span>}
      </td>
      <td style={{ padding: '10px 14px', width: 110 }}>
        {track.genre ? (
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>{track.genre}</span>
        ) : <span style={{ color: '#484f58' }}>—</span>}
      </td>
      <td style={{ padding: '10px 14px', width: 56, textAlign: 'right' }}>
        <span style={{ fontSize: 12, color: '#484f58', fontVariantNumeric: 'tabular-nums' }}>{fmtDuration(track.duration_seconds)}</span>
      </td>
      <td style={{ padding: '10px 14px', width: 18, textAlign: 'center' }}>
        <div title={FP_LABEL[track.fingerprint_status] || 'Unknown'} style={{ width: 7, height: 7, borderRadius: '50%', background: fpColor, margin: '0 auto' }} />
      </td>
      <td style={{ padding: '10px 10px', width: 18, textAlign: 'center' }}>
        <div title={track.status} style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? '#22c55e' : '#484f58', margin: '0 auto' }} />
      </td>
    </tr>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function MusicLibrary() {
  const navigate = useNavigate();
  const [tracks, setTracks]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [editTrack, setEditTrack]   = useState(null);
  const [search, setSearch]         = useState('');
  const [filterGenre, setFilterGenre]   = useState('');
  const [filterMood, setFilterMood]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showUpload, setShowUpload] = useState(false);

  const loadTracks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/music/tracks`, {
        params: { tenant_id: TENANT, track_type: 'music' },
        headers: authHeaders(),
      });
      const data = res.data || [];
      setTracks(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadTracks(); }, [loadTracks]);

  const deleteTrack = async (id) => {
    if (!window.confirm('Delete this track?')) return;
    await axios.delete(`${API}/music/tracks/${id}`, { headers: authHeaders() });
    setSelected(null);
    loadTracks();
  };

  const reScan = async (id) => {
    await axios.post(`${API}/music/tracks/${id}/fingerprint`, {}, { headers: authHeaders() });
    setTimeout(loadTracks, 3000);
  };

  const filtered = tracks.filter(t => {
    const q = search.toLowerCase();
    return (
      (!q || (t.title || '').toLowerCase().includes(q) || (t.artist || '').toLowerCase().includes(q)) &&
      (!filterGenre  || t.genre  === filterGenre) &&
      (!filterMood   || t.mood   === filterMood) &&
      (!filterStatus || t.status === filterStatus)
    );
  });

  const totalDur = tracks.reduce((s, t) => s + (t.duration_seconds || 0), 0);
  const identified = tracks.filter(t => t.fingerprint_status === 'identified').length;

  const iStyle = { background: '#161b22', border: '1px solid #30363d', borderRadius: 7, padding: '6px 11px', color: '#e6edf3', fontSize: 13, outline: 'none' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0d1117', color: '#e6edf3', fontFamily: 'system-ui, sans-serif', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #30363d', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#e6edf3' }}>Music Library</h1>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#8b949e' }}>
              {tracks.length} tracks · {identified}/{tracks.length} identified · {totalDur > 0 ? fmtLibDuration(totalDur) : '—'} total
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={loadTracks} style={{ ...iStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
              <RotateCw size={13} /> Refresh
            </button>
            <button
              onClick={() => setShowUpload(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 7, border: 'none', background: showUpload ? '#21262d' : '#2f81f7', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              <Plus size={14} /> Upload
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            placeholder="Search title or artist…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...iStyle, width: 220 }}
          />
          <select value={filterGenre} onChange={e => setFilterGenre(e.target.value)} style={iStyle}>
            <option value="">All Genres</option>
            {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={filterMood} onChange={e => setFilterMood(e.target.value)} style={iStyle}>
            <option value="">All Moods</option>
            {MOODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={iStyle}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
          </select>
          <span style={{ fontSize: 12, color: '#484f58', marginLeft: 'auto' }}>{filtered.length} shown</span>
        </div>

        {/* Upload zone */}
        {showUpload && (
          <div style={{ marginTop: 12 }}>
            <UploadZone onUploaded={() => { loadTracks(); setShowUpload(false); }} />
          </div>
        )}
      </div>

      {/* Main split panel */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Track table */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#484f58' }}>
              <RotateCw size={20} style={{ animation: 'spin 1s linear infinite', marginRight: 10 }} /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#484f58', gap: 10 }}>
              <AlertCircle size={36} />
              <p style={{ margin: 0, fontSize: 14 }}>{tracks.length === 0 ? 'No tracks yet — upload some music' : 'No tracks match the current filters'}</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <tr style={{ background: '#161b22', borderBottom: '1px solid #30363d' }}>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em' }}>TITLE</th>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em' }}>ARTIST</th>
                  <th style={{ padding: '8px 14px', width: 60, textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em' }}>BPM</th>
                  <th style={{ padding: '8px 14px', width: 100, fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em' }}>MOOD</th>
                  <th style={{ padding: '8px 14px', width: 110, fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em' }}>GENRE</th>
                  <th style={{ padding: '8px 14px', width: 56, textAlign: 'right', fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.06em' }}>DUR</th>
                  <th style={{ padding: '8px 10px', width: 18, textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#8b949e' }} title="ID Status">ID</th>
                  <th style={{ padding: '8px 10px', width: 18, textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#8b949e' }} title="Active Status">ST</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(track => (
                  <TrackRow
                    key={track.id}
                    track={track}
                    selected={selected?.id === track.id}
                    onClick={() => setSelected(selected?.id === track.id ? null : track)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{ width: 320, flexShrink: 0, overflow: 'auto' }}>
            <DetailPanel
              track={selected}
              onEdit={t => setEditTrack(t)}
              onDelete={deleteTrack}
              onReScan={reScan}
              navigate={navigate}
            />
          </div>
        )}
      </div>

      {editTrack && (
        <EditModal
          track={editTrack}
          onSave={() => { setEditTrack(null); loadTracks(); setSelected(null); }}
          onClose={() => setEditTrack(null)}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
